import { useState, useCallback, useRef, useEffect } from "react";
import type { TimelineTransition, TimelineAction } from "@scroll/shared-types/timeline";
import { getTransitionDefinition } from "@scroll/shared-types/transitions";
import TransitionPicker from "./TransitionPicker";

interface TransitionMarkerProps {
  transition: TimelineTransition;
  transitionIndex: number;
  left: number;
  onDispatch: (action: TimelineAction) => void;
}

export default function TransitionMarker({
  transition,
  transitionIndex,
  left,
  onDispatch,
}: TransitionMarkerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const markerRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const def = getTransitionDefinition(transition.type);
  const isCut = transition.type === "cut";

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPicker((prev) => !prev);
  }, []);

  // Close picker on outside click
  useEffect(() => {
    if (!showPicker) return;
    const handleOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    window.addEventListener("mousedown", handleOutside);
    return () => window.removeEventListener("mousedown", handleOutside);
  }, [showPicker]);

  return (
    <div className="transition-marker-wrapper" ref={wrapperRef} style={{ left: `${left}px` }}>
      <button
        ref={markerRef}
        className={`transition-marker${isCut ? "" : " active"}`}
        onClick={handleClick}
        title={`Transition: ${def.label}`}
        aria-label={isCut ? "Add transition between clips" : `Edit ${def.label} transition`}
        aria-haspopup="dialog"
        aria-expanded={showPicker}
      >
        <span className="transition-marker-icon" aria-hidden="true">{isCut ? "+" : def.icon}</span>
      </button>

      {showPicker && (
        <TransitionPicker
          transition={transition}
          transitionIndex={transitionIndex}
          onDispatch={onDispatch}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
